import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@codes/ui-kit/components/ui/dialog';
import { Input } from '@codes/ui-kit/components/ui/input';
import { Label } from '@codes/ui-kit/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { Textarea } from '@codes/ui-kit/components/ui/textarea';
import { toast } from '@codes/ui-kit/components/ui/use-toast';

interface ContactModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ContactModal = ({ isOpen, onClose }: ContactModalProps) => {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        category: '',
        subject: '',
        message: '',
    });

    const categories = [
        { value: 'general', label: t('contact.category.general', 'General Inquiry') },
        { value: 'technical', label: t('contact.category.technical', 'Technical Support') },
        { value: 'billing', label: t('contact.category.billing', 'Billing & Subscription') },
        { value: 'feature', label: t('contact.category.feature', 'Feature Request') },
        { value: 'bug', label: t('contact.category.bug', 'Bug Report') },
    ];

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.email || !formData.message) {
            toast({
                title: t('contact.error.required', 'Required fields missing'),
                description: t('contact.error.requiredDesc', 'Please fill in all required fields.'),
                variant: 'destructive',
            });
            return;
        }

        setIsSubmitting(true);

        try {
            // EmailJS 또는 mailto 폴백
            const emailBody = `
Name: ${formData.name}
Email: ${formData.email}
Category: ${formData.category || 'General'}
Subject: ${formData.subject || 'Contact Inquiry'}

Message:
${formData.message}
            `;

            console.log(emailBody);

            const mailtoUrl = `mailto:louis@lemoncloud.io?subject=${encodeURIComponent(
                formData.subject || `[${formData.category || 'General'}] Contact from ${formData.name}`
            )}&body=${encodeURIComponent(emailBody)}`;
            console.log(mailtoUrl);

            window.open(mailtoUrl);

            toast({
                title: t('contact.success.title', 'Email client opened'),
                description: t('contact.success.desc', 'Your default email client has been opened with the message.'),
            });

            onClose();
            setFormData({ name: '', email: '', category: '', subject: '', message: '' });
        } catch (error) {
            toast({
                title: t('contact.error.title', 'Failed to open email'),
                description: t('contact.error.desc', 'Please try again or contact us directly.'),
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="p-0 max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{t('contact.title', 'Contact Us')}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 p-[18px] pb-10 overflow-auto">
                        <div>
                            <Label variant="form" htmlFor="name">
                                {t('contact.form.name', 'Name')} <span className="text-error">*</span>
                            </Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={e => handleInputChange('name', e.target.value)}
                                placeholder={t('contact.form.namePlaceholder', 'Your name')}
                                required
                            />
                        </div>
                        <div>
                            <Label variant="form" htmlFor="email">
                                {t('contact.form.email', 'Email')} <span className="text-error">*</span>
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={e => handleInputChange('email', e.target.value)}
                                placeholder={t('contact.form.emailPlaceholder', 'your@email.com')}
                                required
                            />
                        </div>

                        <div>
                            <Label variant="form" htmlFor="category">
                                {t('contact.form.category', 'Category')}
                            </Label>
                            <Select
                                value={formData.category}
                                onValueChange={value => handleInputChange('category', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue
                                        placeholder={t('contact.form.categoryPlaceholder', 'Select a category')}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map(category => (
                                        <SelectItem key={category.value} value={category.value}>
                                            {category.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label variant="form" htmlFor="subject">
                                {t('contact.form.subject', 'Subject')}
                            </Label>
                            <Input
                                id="subject"
                                value={formData.subject}
                                onChange={e => handleInputChange('subject', e.target.value)}
                                placeholder={t('contact.form.subjectPlaceholder', 'Brief description of your inquiry')}
                            />
                        </div>

                        <div>
                            <Label variant="form" htmlFor="message">
                                {t('contact.form.message', 'Message')} <span className="text-error">*</span>
                            </Label>
                            <Textarea
                                id="message"
                                value={formData.message}
                                onChange={e => handleInputChange('message', e.target.value)}
                                placeholder={t('contact.form.messagePlaceholder', 'Tell us how we can help you...')}
                                rows={5}
                                required
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2 flex justify-center px-6">
                        <Button type="button" variant="outline" size="lg" onClick={onClose} className="flex-1">
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" disabled={isSubmitting} size="lg" className="flex-1">
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    {t('contact.form.sending', 'Sending...')}
                                </>
                            ) : (
                                <>{t('contact.form.send', 'Send Message')}</>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
